import classNames from 'classnames'
import { FormikErrors, FormikProps, useFormikContext, withFormik } from 'formik'
import { t } from 'i18next'
import React, { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { CountryCode } from '@app/consts/Countries'

import { Form } from '@lib/formik'

import { getRegistrationReferralParams } from '@pages/Referral/helpers'

import { useEntitySettings } from '@context/EntityContext'
import { useModal } from '@context/ModalsContext'
import { SignupFormValues } from '@context/SignupContext'

import { Body1, Body2, Caption1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Checkbox } from '@ui/components/Checkbox'
import { Link } from '@ui/components/Link'
import { PopoverInput } from '@ui/components/Popover'
import { Radio } from '@ui/components/Radio'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { CheckIcon } from '@ui/icons/Check.icon'
import { CloseIcon } from '@ui/icons/Close.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'
import { FlagIcon } from '@ui/icons/flags'
import { IconFCType, IconProps } from '@ui/icons/types'

import {
  TickmillCompaniesEnum,
  isTickmillEUType,
  isTickmillPartnerType,
  isTickmillSCType,
  isTickmillUKType,
} from '@utils/companyName.utils'
import { validateIsTrue } from '@utils/formValidation'
import { minTwoDigits } from '@utils/validations'
import { wait } from '@utils/wait'

import { AccountType } from '@model/AccountType'
import { LegalDocumentDto } from '@model/CompanyLegalDocumentDto'
import { LeadMembersEnum } from '@model/CreateLead'
import { NameDto } from '@model/NameDto'
import {
  RegulatorDto,
  RegulatorOptionDto,
  RegulatorOptionItemDto,
  isRegulatorOptionErrorType,
  isRegulatorOptionSuccessType,
} from '@model/RegulatorOptionDto'
import { TickmillProductType } from '@model/TickmillProductType'

import { createFormCheckBoxField } from '@global/formField/FormCheckBoxField'

import { Box } from '@shared/components/Box'

import {
  AgreedLegalDocument,
  DocumentFieldItem,
  ReadDocumentPage,
  renderCommaSeparated,
} from '../../../Components/DocumentReadAndAgreeField'
import { useProducts } from '../../../hooks/useProducts'
import { ProductSelection } from '../PersonalDetailsStep4/ProductSelection'
import { PersonalDetailsTemplate } from '../PersonalDetailsTemplate'
import { DocumentGroupsFactory } from './DocumentGroupsFactory'
import { RegulatorPopUpModal } from './modals/PersonalDetails.modal'

import styles from '../PersonalDetailsPage.module.scss'

export interface PersonalDetailsStep2FormValues {
  countryId: string
  tickmillCompanyId: number
  productIds: number[]
  optIn: boolean
  singaporeApproachConfirmation?: boolean
  brazilApproachConfirmation?: boolean
  readDocuments: LegalDocumentDto[]
  documents?: { [key: string]: boolean }
  agreedLegalDocuments?: AgreedLegalDocument[]
}

type GroupReference = string | null

interface OuterProps {
  step: number

  countryId?: string
  tickmillCompanyId?: TickmillCompaniesEnum

  regulatorSelected: RegulatorDto | undefined
  regulatorOptions: RegulatorOptionDto[]
  groupReferences: GroupReference[]
  passedDocuments: LegalDocumentDto[]

  formValues?: SignupFormValues

  goBack(): void

  onSubmit(values: PersonalDetailsStep2FormValues): void

  onCountrySelectedChange(regulator: string | undefined): void

  onProductIdsChange?(productIds: number[]): void

  onRegulatorSelectedChange(regulator: RegulatorDto | undefined): void
}

const FormCheckBoxField = createFormCheckBoxField<PersonalDetailsStep2FormValues>()

const PersonalDetailsStep2FormUI: React.FC<
  FormikProps<PersonalDetailsStep2FormValues> & OuterProps
> = (props) => {
  const { isSubmitting, isValid, handleSubmit, setFieldValue, errors, touched, values } = props
  const { regulatorSelected, regulatorOptions, groupReferences, passedDocuments, goBack } = props
  const { t } = useTranslation()
  const [entity] = useEntitySettings()
  const UKPolicyRef = useRef<HTMLDivElement>(null)

  const goBackFromDoc = async () => {
    setFieldValue('readDocuments', [])
    wait(300).then(() => {
      if (UKPolicyRef.current) {
        UKPolicyRef.current.scrollIntoView()
      } else {
        wait(200).then(() => {
          if (UKPolicyRef.current) {
            UKPolicyRef.current.scrollIntoView()
          }
        })
      }
    })
  }

  const { setSelectedProductIds } = useProducts()

  const regulator = useRegulator(props, setSelectedProductIds)
  const redirection = useRedirection(props)
  const isCorporateClient = props.formValues?.typeId === AccountType.Corporate
  const regulatorPopUpModal = useModal(RegulatorPopUpModal)
  const regulatorPopupRegulatorRef = useRef<RegulatorDto | undefined>(undefined)

  useValidationEffect(props)
  useCompanyChangeEffect(props, regulator)

  const handleRegulatorSelected = (data: RegulatorDto | undefined) => {
    regulator.handleRegulatorSelected(data)
  }

  const handleChangeProductIds = (newProductIds: TickmillProductType[]) => {
    setFieldValue('productIds', newProductIds)
    setSelectedProductIds(newProductIds)
    props.onProductIdsChange?.(newProductIds)
  }

  const handleCountryChangeOption = (country: NameDto<string>) => {
    handleRegulatorSelected(undefined)
    setFieldValue('countryId', country.id)
    setFieldValue('tickmillCompanyId', undefined)
    resetPrivacyPolicy()
    props.onCountrySelectedChange(country.id)
    setFieldValue('agreedLegalDocuments', [])
  }

  const countryOptions = regulatorOptions.map((regulatorOption) => regulatorOption.country)
  const [search, setSearch] = useState('')
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)
  const handleSearchClear = () => setSearch('')
  const filteredOptions = useMemo(
    () =>
      countryOptions
        .map((country) => {
          const countryCode = country.id as CountryCode
          return {
            name: country.name,
            id: country.id,
            startIcon: (({ size }: IconProps) => (
              <FlagIcon countryCode={countryCode} size={size} />
            )) as IconFCType,
          }
        })
        .filter((option) => option.name.toLowerCase().includes(search.toLowerCase())),
    [countryOptions, search]
  )
  const handleRegulatorChange = (regulator: RegulatorDto) => {
    if (regulator.redirect) {
      handleRegulatorSelected(regulator)
      if (regulator.popup) {
        regulatorPopupRegulatorRef.current = regulator
        regulatorPopUpModal.open({ regulator, onSubmit: handleRegulatorPopUpModalSubmit })
      } else {
        redirection.redirect(regulator)
      }
    } else {
      handleRegulatorSelected(regulator)
      setFieldValue('tickmillCompanyId', regulator.tickmillCompanyId)
      setFieldValue('agreedLegalDocuments', [])
      resetPrivacyPolicy()
    }
  }

  const handleRegulatorPopUpModalSubmit = () => {
    if (regulatorSelected?.redirect) {
      redirection.redirect(regulatorSelected)
    } else {
      const selectedRegulator = regulatorPopupRegulatorRef.current
      if (selectedRegulator) {
        regulator.handleRegulatorSelected(selectedRegulator)
        setFieldValue('tickmillCompanyId', selectedRegulator.tickmillCompanyId)
      }
    }
    regulatorPopUpModal.close()
  }

  const resetPrivacyPolicy = () => {
    setFieldValue('documents', {})
    setFieldValue('optIn', false)
  }

  const hasProductIdsError = errors.productIds && touched.productIds
  const productIdsError = hasProductIdsError
    ? Array.isArray(errors.productIds)
      ? errors.productIds[0]
      : errors.productIds
    : undefined

  const isTickmillUK = regulatorSelected?.tickmillCompanyId === TickmillCompaniesEnum.TICKMILL_UK
  const UKTermsOfBusiness = useMemo(
    () =>
      isTickmillUK
        ? passedDocuments.find(
            (d) => d.code === 'terms_of_business' && d.groupReference === 'UKStocksConsolidated'
          )
        : undefined,
    [isTickmillUK, passedDocuments]
  )

  const defaultPrivacyPolicy = useMemo(
    () =>
      isTickmillUK
        ? passedDocuments.find((d) => d.code === 'privacy_policy' && d.groupReference === null)
        : undefined,
    [isTickmillUK, passedDocuments]
  )

  const UKPrivacyPolicy = useMemo(
    () =>
      isTickmillUK
        ? passedDocuments.find(
            (d) => d.code === 'privacy_policy' && d.groupReference === 'UKStocksConsolidated'
          )
        : defaultPrivacyPolicy,
    [isTickmillUK, passedDocuments, defaultPrivacyPolicy]
  )

  const stockPrivacyPolicy = useMemo(() => {
    if (isCorporateClient) {
      return undefined
    }

    const isUKCompany =
      (values.tickmillCompanyId || regulatorSelected?.tickmillCompanyId) ===
      TickmillCompaniesEnum.TICKMILL_UK
    const isStocksSelected = values.productIds.includes(TickmillProductType.STOCKS)

    if (isUKCompany && isStocksSelected) {
      return passedDocuments.find((d) => d.code === 'terms_of_business')
    }
    return undefined
  }, [
    isCorporateClient,
    values.tickmillCompanyId,
    values.productIds,
    regulatorSelected?.tickmillCompanyId,
    passedDocuments,
  ])

  const groupedPrivacyPolicies = useMemo(() => {
    const isUKCompany =
      (values.tickmillCompanyId || regulatorSelected?.tickmillCompanyId) ===
      TickmillCompaniesEnum.TICKMILL_UK

    if (isUKCompany && UKPrivacyPolicy && stockPrivacyPolicy) {
      const ukGroupRef = UKPrivacyPolicy.groupReference
      const stockGroupRef = stockPrivacyPolicy.groupReference

      if (ukGroupRef && stockGroupRef && ukGroupRef === stockGroupRef) {
        return [UKPrivacyPolicy, stockPrivacyPolicy]
      }
    }
    return []
  }, [
    UKPrivacyPolicy,
    stockPrivacyPolicy,
    values.tickmillCompanyId,
    regulatorSelected?.tickmillCompanyId,
  ])

  useEffect(() => {
    if (groupedPrivacyPolicies.length > 0) {
      const groupReference = groupedPrivacyPolicies[0]?.groupReference
      if (groupReference) {
        const allDocumentsAccepted = groupedPrivacyPolicies.every(
          (doc) => values.documents?.[doc.id] === true
        )
        if (allDocumentsAccepted && values.documents?.[groupReference] !== true) {
          setFieldValue(`documents.${groupReference}`, true)
        }
      }
    }
  }, [groupedPrivacyPolicies, values.documents, setFieldValue])

  const regularDocuments = useMemo(
    () =>
      passedDocuments.filter(
        ({ id, groupReference }) =>
          id !== UKPrivacyPolicy?.id &&
          id !== UKTermsOfBusiness?.id &&
          id !== defaultPrivacyPolicy?.id &&
          groupReference !== 'UKStocksConsolidated'
      ),
    [UKPrivacyPolicy?.id, UKTermsOfBusiness?.id, defaultPrivacyPolicy?.id, passedDocuments]
  )

  useEffect(() => {
    props.validateForm()
  }, [passedDocuments])

  if (values?.readDocuments && values.readDocuments.length > 0) {
    return <ReadDocumentPage goBackFromDoc={goBackFromDoc} />
  }

  const regularProducts = regulatorSelected?.products.filter(
    (product) => product.id !== TickmillProductType.STOCKS
  )

  const isStockProductAvailable = regulatorSelected?.products.filter(
    (product) => product.id === TickmillProductType.STOCKS
  )

  const stockProducts = !isCorporateClient && isStockProductAvailable

  return (
    <PersonalDetailsTemplate step={2} goBack={goBack} reversed>
      <Form className={styles.form} onSubmit={handleSubmit}>
        <Heading6 medium>{t('Sign up.Country of Residence')}</Heading6>
        <PopoverInput
          name='countryId'
          label={t('Sign up.Country')}
          value={values.countryId as CountryCode}
          required
          options={filteredOptions}
          searchInput={{
            value: search,
            handleChange: handleSearchChange,
            handleClear: handleSearchClear,
          }}
          onChange={handleCountryChangeOption}
        />
        {!isTickmillPartnerType(entity.entity) && (
          <React.Fragment>
            <Heading6 medium>{t('Sign up.Legal Entity')}</Heading6>
            <Body1 isParagraph>
              {t(
                'Sign up.Based on your country of residence, you can register under the following entities'
              )}
              .
            </Body1>
            {regulator.regulators.map((regulator, index) => (
              <RegulatorField
                regulator={regulator}
                onChange={handleRegulatorChange}
                isCorporateClient={isCorporateClient}
                key={index}
              />
            ))}
          </React.Fragment>
        )}
        {regulatorSelected?.products?.length !== undefined && regularProducts && (
          <ProductSelection
            products={regularProducts}
            productIds={values.productIds}
            onProductChange={handleChangeProductIds}
            title={t('Sign up.Select Product')}
            error={productIdsError || ''}
            className={styles.textSmall}
          />
        )}
        {stockProducts && stockProducts.length > 0 && (
          <ProductSelection
            products={stockProducts}
            productIds={values.productIds}
            onProductChange={handleChangeProductIds}
            title={t('Sign up.Other investment products')}
          />
        )}

        <Heading6 medium>{t('Sign up.Privacy Policy')}</Heading6>
        {groupedPrivacyPolicies.length > 0 ? (
          <DocumentGroupsFactory
            renderItem={(props: DocumentFieldItem) => (
              <div ref={UKPolicyRef} className={styles.ukTerms}>
                <Checkbox
                  onChange={props.isAccepted ? () => undefined : props.onClick}
                  checked={props.isAccepted}
                />
                <Body1>
                  {t('Validation.I have read and accepted the')} {''}
                  {renderCommaSeparated(
                    groupedPrivacyPolicies.map((doc) => (
                      <Link
                        key={doc.id}
                        onClick={
                          props.isAccepted
                            ? () => window.open(doc.url || '', '_blank', 'noreferrer')
                            : props.onClick
                        }
                      >
                        {doc.title}
                      </Link>
                    ))
                  )}
                </Body1>
              </div>
            )}
            groupReferences={groupReferences}
            documents={groupedPrivacyPolicies}
          />
        ) : stockPrivacyPolicy?.title ? (
          <DocumentGroupsFactory
            renderItem={(props: DocumentFieldItem) => (
              <div ref={UKPolicyRef} className={styles.ukTerms}>
                <Checkbox
                  onChange={props.isAccepted ? () => undefined : props.onClick}
                  checked={props.isAccepted}
                />
                <Body1>
                  {t('Validation.I have read and accepted the')}{' '}
                  <Link
                    onClick={
                      props.isAccepted
                        ? () => window.open(stockPrivacyPolicy.url || '', '_blank', 'noreferrer')
                        : props.onClick
                    }
                  >
                    {stockPrivacyPolicy.title}
                  </Link>
                </Body1>
              </div>
            )}
            groupReferences={groupReferences}
            documents={[stockPrivacyPolicy]}
          />
        ) : UKPrivacyPolicy?.title && UKPrivacyPolicy?.groupReference ? (
          <DocumentGroupsFactory
            renderItem={(props: DocumentFieldItem) => (
              <div ref={UKPolicyRef} className={styles.ukTerms}>
                <Checkbox
                  onChange={props.isAccepted ? () => undefined : props.onClick}
                  checked={props.isAccepted}
                />
                <Body1>
                  {t('Validation.I have read and accepted the')}{' '}
                  <Link
                    onClick={
                      props.isAccepted
                        ? () => window.open(UKPrivacyPolicy.url || '', '_blank', 'noreferrer')
                        : props.onClick
                    }
                  >
                    {UKPrivacyPolicy.title}
                  </Link>
                </Body1>
              </div>
            )}
            groupReferences={groupReferences}
            documents={[UKPrivacyPolicy]}
          />
        ) : isTickmillUK && defaultPrivacyPolicy?.groupReference === null ? (
          <DocumentGroupsFactory
            renderItem={(props: DocumentFieldItem) => (
              <div ref={UKPolicyRef} className={styles.ukTerms}>
                <Checkbox
                  onChange={props.isAccepted ? () => undefined : props.onClick}
                  checked={props.isAccepted}
                />
                <Body1>
                  {t('Validation.I have read and accepted the')}{' '}
                  <Link
                    onClick={
                      props.isAccepted
                        ? () => window.open(defaultPrivacyPolicy.url || '', '_blank', 'noreferrer')
                        : props.onClick
                    }
                  >
                    {defaultPrivacyPolicy.title}
                  </Link>
                </Body1>
              </div>
            )}
            groupReferences={groupReferences}
            documents={[defaultPrivacyPolicy]}
          />
        ) : null}
        {regularDocuments.length >= 1 && (
          <DocumentGroupsFactory groupReferences={groupReferences} documents={regularDocuments} />
        )}

        {values.countryId?.toUpperCase() === 'BRA' && (
          <div className='control pt-1'>
            <BrazilApproachConfirmationField />
          </div>
        )}

        {values.countryId?.toUpperCase() === 'SGP' && (
          <div className='control pt-1'>
            <SingaporeApproachConfirmationField />
          </div>
        )}

        <div className='control pt-1'>
          <FieldCheckBoxOptInField />
        </div>
        <div className={styles.button}>
          <Button.Primary
            size='lg'
            type='submit'
            fullWidth
            iconRight={ArrowIcon.Right}
            disabled={isSubmitting || !isValid}
          >
            {t('Next')}
          </Button.Primary>
        </div>
      </Form>
    </PersonalDetailsTemplate>
  )
}

interface RegulatorFieldProps {
  regulator: RegulatorDto
  isCorporateClient: boolean

  onChange(regulator: RegulatorDto): void
}

const RegulatorField: React.FC<RegulatorFieldProps> = (props) => {
  const { regulator, onChange, isCorporateClient } = props

  const { values } = useFormikContext<PersonalDetailsStep2FormValues>()

  const handleClick = () => onChange(regulator)

  const filteredOptions = useMemo(() => {
    if (isCorporateClient) {
      return regulator.options.filter((option) => option.optionText !== 'Stocks')
    }
    return regulator.options
  }, [regulator.options, isCorporateClient])

  return (
    <div className={classNames('field', styles.regulator)} onClick={handleClick}>
      <div
        className={classNames('control', {
          [styles.isActive]: values.tickmillCompanyId === regulator.tickmillCompanyId,
        })}
      >
        <Box flex direction='column' p='x24' gap='x16'>
          <Radio
            name='tickmillCompanyId'
            onChange={handleClick}
            checked={values.tickmillCompanyId === regulator.tickmillCompanyId}
            key={regulator.name}
          >
            <Box flex direction='column' gap='x16'>
              <Body1>{regulator.title}</Body1>
              <Body2 isParagraph className='text-secondary'>
                {regulator.name}
              </Body2>
            </Box>
          </Radio>

          {filteredOptions.length > 0 && (
            <Box flex direction='column'>
              <div className={styles.regulatorDetails}>
                {filteredOptions.map((x, index) => (
                  <div className={styles.optionWrapper} key={index}>
                    {x.showIcon && (
                      <Body1>
                        <RegulatorOptionIcon {...x} />
                      </Body1>
                    )}
                    {x.showHeaderString && <Body1>{x.headerString}</Body1>}
                    <Caption1 className={styles.centerSmallText}>{x.optionText}</Caption1>
                  </div>
                ))}
              </div>
            </Box>
          )}

          {regulator.warnings.length > 0 &&
            regulator.warnings.map((warning) => (
              <Body2 key={warning}>
                <WarningIcon size={20} color='text-warning-high-em' /> {warning}
              </Body2>
            ))}
        </Box>
      </div>
    </div>
  )
}

const FieldCheckBoxOptInField = (): JSX.Element => {
  const { t } = useTranslation()
  const [entity] = useEntitySettings()

  return (
    <FormCheckBoxField name='optIn'>
      {isTickmillEUType(entity.entity) && (
        <Body1>
          {t('Sign up.I agree to submit my information to Tickmill Europe Ltd to communicate')}
        </Body1>
      )}
      {isTickmillUKType(entity.entity) && (
        <Body1>
          {t('Sign up.I agree to submit my information to Tickmill UK Ltd to communicate')}
        </Body1>
      )}
      {isTickmillSCType(entity.entity) && (
        <Body1>
          {t(
            'Sign up.I agree to receive Tickmill marketing communications via email, WhatsApp, and other relevant channels'
          )}
        </Body1>
      )}
      {!isTickmillSCType(entity.entity) &&
        !isTickmillEUType(entity.entity) &&
        !isTickmillUKType(entity.entity) && (
          <Body1>{t('Sign up.I agree to submit my information to Tickmill to communicate')}</Body1>
        )}
    </FormCheckBoxField>
  )
}

const SingaporeApproachConfirmationField = (): JSX.Element => {
  const { t } = useTranslation()
  return (
    <FormCheckBoxField
      name='singaporeApproachConfirmation'
      validate={validateIsTrue(t('Validation.Required'))}
    >
      <Body1>
        {t(
          'Sign up.I confirm that I approached Tickmill on my own and was not solicited in Singapore'
        )}{' '}
        <Body1 className='text'>*</Body1>
      </Body1>
    </FormCheckBoxField>
  )
}

const BrazilApproachConfirmationField = (): JSX.Element => {
  const { t } = useTranslation()
  return (
    <FormCheckBoxField
      name='brazilApproachConfirmation'
      validate={validateIsTrue(t('Validation.Required'))}
    >
      <Body1>
        {t(
          'Sign up.I confirm that I approached Tickmill on my own and was not solicited in Brazil'
        )}{' '}
        <Body1 className='text'>*</Body1>
      </Body1>
    </FormCheckBoxField>
  )
}

const RegulatorOptionIcon: React.FC<RegulatorOptionItemDto> = (props) => (
  <React.Fragment>
    {isRegulatorOptionSuccessType(props) && <CheckIcon size={18} color='text-success-high-em' />}
    {isRegulatorOptionErrorType(props) && <CloseIcon size={18} color='text-danger-high-em' />}
  </React.Fragment>
)

const initialState = (props: OuterProps): PersonalDetailsStep2FormValues => {
  const searchParams = new URLSearchParams(window.location.search)

  const countryId = props.countryId || ''
  const tickmillCompanyId =
    props?.formValues?.tickmillCompanyId ||
    Number(searchParams.get(LeadMembersEnum.TICKMILL_COMPANY_ID)) ||
    0
  const optIn = props?.formValues?.optIn || false
  const singaporeApproachConfirmation = props?.formValues?.singaporeApproachConfirmation || false
  const brazilApproachConfirmation = props?.formValues?.brazilApproachConfirmation || false
  const productIds = props?.formValues?.productIds || []
  const agreedLegalDocuments: AgreedLegalDocument[] = []
  const readDocuments: LegalDocumentDto[] = []
  const documents = {}

  return {
    ...props?.formValues,
    countryId,
    tickmillCompanyId,
    optIn,
    singaporeApproachConfirmation,
    brazilApproachConfirmation,
    productIds,
    agreedLegalDocuments,
    readDocuments,
    documents,
  }
}

export const PersonalDetailsStep2Form = withFormik<OuterProps, PersonalDetailsStep2FormValues>({
  mapPropsToValues: (props) => {
    return initialState(props)
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, { passedDocuments, regulatorSelected }) => {
    const errors: FormikErrors<PersonalDetailsStep2FormValues> = {}
    const { agreedLegalDocuments = [], productIds, documents = {} } = values

    const requiredDocuments = getRequiredLegalDocuments(passedDocuments, values, regulatorSelected)

    if (!requiredDocuments.length) {
      errors.agreedLegalDocuments = t('Validation.Required')
    } else {
      if (!isDocumentsPositiveValue(documents)) {
        errors.agreedLegalDocuments = t('Validation.Required')
      }

      if (!areRequiredDocumentsChecked(requiredDocuments, documents)) {
        errors.agreedLegalDocuments = t('Validation.Required')
      }

      if (!areRequiredDocumentsAgreed(requiredDocuments, agreedLegalDocuments)) {
        errors.agreedLegalDocuments = t('Validation.Required')
      }
    }

    if (
      !isTickmillPartner(regulatorSelected) &&
      !isProductSelected(regulatorSelected, productIds)
    ) {
      errors.productIds = t('Validation.Please select product(s)')
    }

    return errors
  },

  validateOnMount: true,
  validateOnBlur: true,
  validateOnChange: true,
  enableReinitialize: false,
})(PersonalDetailsStep2FormUI)

const isTickmillPartner = (regulatorSelected?: RegulatorDto) => {
  return isTickmillPartnerType(regulatorSelected?.tickmillCompanyId)
}

const isProductSelected = (regulatorSelected?: RegulatorDto, productIds: number[] = []) => {
  if (regulatorSelected?.products?.length !== undefined) {
    return productIds.length
  }
  return true
}

const isDocumentsPositiveValue = (documents: { [key: string]: boolean } = {}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return Object.entries(documents || []).every(([_, value]) => value)
}

const getRequiredLegalDocuments = (
  allDocuments: LegalDocumentDto[] = [],
  values: PersonalDetailsStep2FormValues,
  regulatorSelected?: RegulatorDto
): LegalDocumentDto[] => {
  const isUKCompany =
    (values.tickmillCompanyId || regulatorSelected?.tickmillCompanyId) ===
    TickmillCompaniesEnum.TICKMILL_UK

  const isStocksSelected = values.productIds?.includes(TickmillProductType.STOCKS)

  if (!isUKCompany) {
    return allDocuments
  }

  const hasUKStocksGroup = allDocuments.some((doc) => doc.groupReference === 'UKStocksConsolidated')

  if (!hasUKStocksGroup) {
    return allDocuments
  }

  if (isStocksSelected) {
    return allDocuments
  }

  return allDocuments.filter((doc) => {
    if (doc.groupReference !== 'UKStocksConsolidated') return true
    return doc.code === 'privacy_policy'
  })
}

const areRequiredDocumentsChecked = (
  requiredDocuments: LegalDocumentDto[] = [],
  documentsMap: { [key: string]: boolean } = {}
): boolean => {
  if (!requiredDocuments.length) return false

  return requiredDocuments.every((doc) => {
    const checkedById = documentsMap[doc.id] === true
    const checkedByGroup =
      doc.groupReference != null ? documentsMap[doc.groupReference] === true : false

    return checkedById || checkedByGroup
  })
}

const areRequiredDocumentsAgreed = (
  requiredDocuments: LegalDocumentDto[] = [],
  agreedLegalDocuments: AgreedLegalDocument[] = []
): boolean => {
  if (!requiredDocuments.length) return false

  return requiredDocuments.every((doc) =>
    agreedLegalDocuments.some((agreedDoc) => agreedDoc.id === doc.id)
  )
}

interface RegulatorProps {
  handleRegulatorSelected(regulator: RegulatorDto | undefined, callback?: () => void): void

  regulators: RegulatorDto[]
}

const useRegulator = (
  props: FormikProps<PersonalDetailsStep2FormValues> & OuterProps,
  setProductIds: React.Dispatch<React.SetStateAction<TickmillProductType[]>>
): RegulatorProps => {
  const { values, setFieldValue, regulatorOptions } = props

  const [, setEntity] = useEntitySettings()

  const handleRegulatorSelected = useCallback(
    (regulator: RegulatorDto | undefined) => {
      props.onRegulatorSelectedChange(regulator)
      if (regulator) {
        setEntity({ entity: regulator.tickmillCompanyId })
        setFieldValue('productIds', [])
        setFieldValue('tickmillCompanyId', regulator.tickmillCompanyId)
        setProductIds([])
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setEntity, setFieldValue, props.onRegulatorSelectedChange, setProductIds]
  )

  const getRegulator = useCallback(() => {
    return (
      (regulatorOptions || []).find(
        (x) =>
          x.country.id.toUpperCase() === values.countryId.toUpperCase() ||
          x.country.shortName?.toUpperCase() === values.countryId.toUpperCase()
      )?.regulators || []
    )
  }, [regulatorOptions, values.countryId])

  useEffect(() => {
    if (!props.regulatorSelected && values.tickmillCompanyId >= 1) {
      const matchingRegulatorOption = regulatorOptions.find((regulatorOption) => {
        return (
          (regulatorOption.country.id.toUpperCase() === values.countryId.toUpperCase() ||
            regulatorOption.country.shortName?.toUpperCase() === values.countryId.toUpperCase()) &&
          regulatorOption.regulators.some(
            (regulator) => regulator.tickmillCompanyId === values.tickmillCompanyId
          )
        )
      })
      const matchingRegulator = matchingRegulatorOption?.regulators.find(
        (regulator) => regulator.tickmillCompanyId === values.tickmillCompanyId
      )
      if (matchingRegulator) {
        handleRegulatorSelected(matchingRegulator)
      }
    }
  }, [
    values.tickmillCompanyId,
    regulatorOptions,
    props.regulatorSelected,
    handleRegulatorSelected,
    values.countryId,
  ])

  return { handleRegulatorSelected, regulators: getRegulator() }
}

const useCompanyChangeEffect = (
  props: FormikProps<PersonalDetailsStep2FormValues> & OuterProps,
  regulator: RegulatorProps,
  regulatorSelected?: RegulatorDto
) => {
  const { setFieldValue } = props
  const { handleRegulatorSelected } = regulator

  const [entity] = useEntitySettings()

  useEffect(() => {
    if (entity.entity === TickmillCompaniesEnum.TICKMILL_PA) {
      setFieldValue('tickmillCompanyId', TickmillCompaniesEnum.TICKMILL_PA)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity.entity, setFieldValue])

  useEffect(() => {
    const preSelected = regulator.regulators.find((x) => x.preSelected)
    if (preSelected && !regulatorSelected) {
      handleRegulatorSelected(preSelected)
      setFieldValue('tickmillCompanyId', preSelected.tickmillCompanyId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regulator.regulators])
}

const useValidationEffect = (props: FormikProps<PersonalDetailsStep2FormValues> & OuterProps) => {
  useEffect(() => {
    props.validateForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.values.optIn,
    props.values.documents,
    props.values.singaporeApproachConfirmation,
    props.values.brazilApproachConfirmation,
    props.values.productIds,
    props.values.agreedLegalDocuments,
  ])
}

const useRedirection = (props: FormikProps<PersonalDetailsStep2FormValues> & OuterProps) => {
  const { values, formValues } = props

  const [params] = useSearchParams()

  const getRedirectionLink = (link: string) => {
    const referral = getRegistrationReferralParams(params.toString())

    const getBirthday = () => {
      const { yearOfBirth, monthOfBirth, dayOfBirth } = formValues || {}
      if (yearOfBirth && monthOfBirth && dayOfBirth) {
        return `${yearOfBirth}-${minTwoDigits(monthOfBirth)}-${minTwoDigits(dayOfBirth)}`
      }
      return ''
    }

    params.set('countryId', values.countryId || formValues?.countryId || '')

    params.set('tickmillCompanyId', formValues?.tickmillCompanyId?.toString() || '')
    params.set(LeadMembersEnum.GENDER_ID, formValues?.genderId || '')
    params.set(LeadMembersEnum.TYPE_ID, formValues?.typeId.toString() || '')
    params.set(LeadMembersEnum.FIRST_NAME, formValues?.firstName || '')
    params.set(LeadMembersEnum.MIDDLE_NAME, formValues?.middleName || '')
    params.set(LeadMembersEnum.LAST_NAME, formValues?.lastName || '')
    params.set(LeadMembersEnum.NATIVE_NAME, formValues?.nativeName || '')
    params.set(
      LeadMembersEnum.COUNTRY_CODE,
      formValues?.countryCode || params.get(LeadMembersEnum.COUNTRY_CODE) || ''
    )
    params.set(
      LeadMembersEnum.PHONE_NUMBER,
      formValues?.phoneNumber || params.get(LeadMembersEnum.PHONE_NUMBER) || ''
    )

    params.set(LeadMembersEnum.BIRTHDAY, getBirthday())

    params.set(LeadMembersEnum.EMAIL, formValues?.email || params.get(LeadMembersEnum.EMAIL) || '')
    params.set(
      LeadMembersEnum.LANGUAGE_ID,
      formValues?.languageId || params.get(LeadMembersEnum.LANGUAGE_ID) || ''
    )

    Object.entries(referral).map(([k, v]) => params.set(k.toString(), v.toString()))

    return `${link}&${params.toString()}`
  }

  const redirect = (regulator?: RegulatorDto, regulatorSelected?: RegulatorDto) => {
    window.location.replace(
      getRedirectionLink(regulator ? regulator.link : regulatorSelected?.link || '')
    )
  }

  return { getRedirectionLink, redirect }
}
